import { AfterViewInit, ChangeDetectorRef, Component, Injector, Input, OnInit, TemplateRef, Type, ViewChild } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { OptionType } from 'ng/src/app/core/Option';
import { ResultType } from 'ng/src/app/core/Result';
import { AppStateService } from 'ng/src/app/core/services/app-state.service';
import { MultiSortConfig, Utils } from 'ng/src/app/core/utils';
import { ComputedValue, HasValue, ValueAlias } from 'ng/src/app/core/value';
import { BaseComponent } from 'ng/src/app/shared/base.component';
import { CommandPaletteAction, CommandPaletteRegistration } from 'ng/src/app/shared/command-palette/command-palette.service';
import { BooleanInputDialogEntry, FormInputDialogEntryMap, FormInputDialogForm, MapTypeCallback, SelectorInputDialogEntry, StringInputDialogEntry } from 'ng/src/app/shared/form-input-dialog/form-input-dialog.model';
import { FormInputDialogService } from 'ng/src/app/shared/form-input-dialog/form-input-dialog.service';
import { SelectorItem } from 'ng/src/app/shared/item-selectors/item-selector.model';
import { fromMediaQuery, MediaQueries } from 'ng/src/app/shared/media-query-observable';
import { BasicRecord, JSON, Nullable, Nullish, Optional } from 'ng/src/app/shared/model/app.model';
import { TypedTreeNode } from 'ng/src/app/shared/model/primeng-extensions.model';
import { ClipboardTreeNode, TemplateSegmentClipboardService } from 'ng/src/app/templates/clipboard/template-segment-clipboard.service';
import { TemplateEditorService } from 'ng/src/app/templates/editor/template-editor.service';
import { FormulaEditorScope } from 'ng/src/app/templates/formula-editor/formula-editor.component';
import { doesTemplateSegmentPropertiesSupportProperty, TemplateSegmentPropertyKey } from 'ng/src/app/templates/segment-editor/segment-editor-form';
import { SegmentEditorComponent } from 'ng/src/app/templates/segment-editor/segment-editor.component';
import { TemplateSegmentEditorType } from 'ng/src/app/templates/segment-editor/segment-editor.service';
import { ReadonlyContainerEntry, TemplateContainer } from 'ng/src/app/templates/shared/template-container/template-container.model';
import { Formula } from 'ng/src/app/templates/shared/template-value-calculator.model';
import { TemplateService } from 'ng/src/app/templates/shared/template.service';
import { LogUser } from 'ng/src/app/users/shared/user.model';
import { ConfirmationService, MenuItem, TreeDragDropService } from 'primeng/api';
import { Dialog } from 'primeng/dialog';
import { Dropdown } from 'primeng/dropdown';
import { Tree } from 'primeng/tree';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { catchError, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { TemplateSegmentTypeOrder, TemplateSegment, TemplateSegmentType, TemplateSegmentTypeId, TemplateTypeId, TemplateWithUsers, TypedTemplateSegment, TypedTemplateSegmentType } from '../shared/template.model';


@Component({
	selector: 'lb-template-editor[template][logTimeZone]',
	templateUrl: './template-editor.component.html',
	styleUrls: ['./template-editor.component.scss'],
	providers: [
		ConfirmationService,
		TreeDragDropService,
		TemplateEditorService
	]
})
export class TemplateEditorComponent extends BaseComponent implements OnInit, AfterViewInit {
	@ViewChild('calculationTemplate') calculationTemplate!: TemplateRef<any>;
	@ViewChild('segmentEditor') segmentEditor!: SegmentEditorComponent;
	@ViewChild('newSegmentDropdown') dropdown!: Dropdown;
	@ViewChild('newSegmentDialog') dialog!: Dialog;
	@ViewChild(Tree) uiTree!: Tree;
	
	@Input()
	set template(val: TemplateWithUsers) {
		if (this.hadInit)
			this.templateChangeSubject.next(val);
		
		this._template = val;
	}
	
	get template() {
		return this._template;
	}
	
	@Input()
	logTimeZone!: string;
	
	private _template!: TemplateWithUsers;
	private _segments?: TemplateSegment[];
	private _segmentRefresh?: Date;
	
	private static readonly preferenceKey = 'template-editor.preferences';
	segmentOpenMap: Record<string, boolean> = OptionType.of(
		AppStateService.preferences.getValue(TemplateEditorComponent.preferenceKey, {})
	)
		.castAsType((val): val is Record<string, boolean> => {
			return typeof val === 'object' && val != null && Object.values(val).every(v => typeof v === 'boolean');
		})
		.fork({
			some: (val) => {
				return val;
			},
			none: () => {
				return {} as Record<string, boolean>;
			}
		})
		.map(obj => {
			return Utils.createChangeTrackingProxy(obj, (k, v, oV, obj) => {
				AppStateService.preferences.setValue(TemplateEditorComponent.preferenceKey, obj);
			});
		})
		.getOrThrow(new Error('Unable to create segmentOpenMap'));
	
	private nodeMap: BasicRecord<HasValue<EditorTreeNode>> = {};
	
	private segmentMenuMap: BasicRecord<ComputedValue<Record<MenuType, MenuItem[]>, EditorTreeNode>> = {};
	private sectionMenuMap: BasicRecord<ComputedValue<MenuItem[], EditorTreeNode>> = {};
	
	readonly hasSubTemplates: ComputedValue<boolean, Optional<TemplateContainer>>;
	readonly lastSectionNode: ComputedValue<Nullable<EditorTreeNode>, EditorTreeNode[]>;
	
	private hadInit = false;
	private _newSegmentType?: TemplateSegmentType;
	private _newPrevId?: number;
	private _containerArray: Array<ReadonlyContainerEntry> = [];
	private _showObsolete = false;
	
	private _templateContainerSubject = new BehaviorSubject<TemplateContainer | undefined>(undefined);
	
	segmentMap: BasicRecord<TemplateSegment> = {};
	formulaMap: Record<string, Formula.Scope> = {};
	
	highlightSegments = new Set<number>();
	scrollToSegment?: number;
	
	readonly TemplateSegment: Type<TemplateSegment> = TemplateSegment as any;
	readonly EditorTreeNode: EditorTreeNode = null as any;
	
	readonly SEGMENT_TYPE = TemplateSegmentTypeId;
	
	readonly dialogVisible = new ValueAlias(() => !!this.dialogTemplate, val => {
		if (!val) delete this.dialogTemplate
	});
	
	readonly confirmPopupKey = Utils.generateUniqueId();
	
	readonly hoverSupported$ = fromMediaQuery(MediaQueries.HOVER_SUPPORTED);
	
	
	readonly segmentTypes = this.templateService.getTemplateSegmentTypes().pipe(
		map(res => res._embedded.templateSegmentTypes
			.filter(t => t.id !== TemplateSegmentTypeId.SUB_TEMPLATE)
			.sort(Utils.createMultiSortFunction([
				{
					key: (link) => TemplateSegmentTypeOrder.indexOf(link.id)
				},
				{ key: 'name' }
			]))
			.map(t => SelectorItem.with(t, t.name))
		)
	)
	
	constructor(
		private templateService: TemplateService,
		private confirmationService: ConfirmationService,
		private segmentClipboard: TemplateSegmentClipboardService,
		private templateEditorService: TemplateEditorService,
		private formDialogService: FormInputDialogService,
		private changeDetectorRef: ChangeDetectorRef,
		protected injector: Injector
	) {
		super(injector);
		this.templateEditorService.templateEditor = this;
		this.hasSubTemplates = new ComputedValue({
			reference: () => this.templateContainer,
			get: (container) => {
				if (container == null || container.templateTypeId === TemplateTypeId.INSPECTION) {
					return false;
				}
				
				return container.entries.some(e => e.segment?.type?.id === TemplateSegmentTypeId.SUB_TEMPLATE);
			}
		});
		
		this.lastSectionNode = new ComputedValue({
			reference: () => this.treeNodes,
			get: (val) => {
				if (!val.length) {
					return null;
				}
				
				return [...this.treeNodes].reverse().find((node) => node.data?.type?.id === TemplateSegmentTypeId.SUB_TEMPLATE || node.data?.id == null) ?? null;
			}
		})
	}
	
	loading = false;
	hadError = false;
	
	templateChangeSubject = new Subject<TemplateWithUsers>();
	
	dialogTemplate?: TemplateRef<any>;
	dialogContext = Nullable.of<DialogContext>();
	
	get selected() {
		return this.segmentClipboard.value;
	}
	
	set selected(val) {
		this.segmentClipboard.value = val.filter(v => v.selectable);
		console.log('selected', this.segmentClipboard.value);
	}
	
	get templateType() {
		return this._template.templateType.id;
	}
	
	get newSegmentType() {
		return this._newSegmentType;
	}
	set newSegmentType(val) {
		this.setNewSegmentType(val);
	}
	
	get newPrevId() {
		return this._newPrevId;
	}
	
	set newPrevId(val) {
		this._newPrevId = val;
	}
	
	get templateContainer() {
		return this._templateContainerSubject.value;
	}
	
	set templateContainer(val) {
		this._templateContainerSubject.next(val);
		if (val == null) {
			this._containerArray = [];
			this.segmentMap = {};
			this.formulaMap = {};
			return;
		}
		
		this._containerArray = (!this.showObsolete ? val.getNonObsoleteView().entries : val.entries) as ReadonlyContainerEntry[];
		
		const segmentsArray = (val?.flatten() ?? [])
			.map(i => i.segment)
			.filter(Nullable.isNotNull);
		
		this.segmentMap = Utils.arrayToMap(segmentsArray, 'id');
		
		this.formulaMap = Utils.arrayToMap(
			segmentsArray
				.filter(item => item.calculate)
				.map(segment => {
					const scope = ResultType.tryCatch(() => Formula.parseFormula(segment))
						.match({
							value: (e) => e,
							error: (e) => {
								if (e instanceof Error) {
									console.error('Error parsing formula', e);
								}
								
								return Formula.emptyScope();
							}
						});
					
					return { scope, segment };
				}),
			({ segment, scope }) => [segment.id, scope]
		);
	}
	
	get treeNodes() {
		return this.templateEditorService.treeNodes;
	}
	
	get hasObsolete() {
		return this.templateContainer?.hasHiddenObsoleteEntries() ?? false;
	}
	
	get showObsolete() {
		return this._showObsolete;
	}
	
	set showObsolete(val) {
		if (this._showObsolete === val) return;
		this._showObsolete = val;
		this.updateContainerFromSegments();
	}
	
	clipboardHasSelection = false;
	
	clipboardHasSection = false;
	
	ngOnInit(): void {
		const tags = ['create', 'segment', 'template', 'new'];
		this.segmentTypes.pipe(
			takeUntil(this.onDestroy$),
			take(1),
			map((s) => s.map((e) => e.value))
		)
			.subscribe(segments => {
				const actions = segments.map(s => {
					return {
						group: 'Template Segments',
						icon: this.Icons.PLUS,
						label: `${s.name}`,
						tags,
						title: `Create new ${s.name} segment`,
						command: () => {
							this.newPrevId = Utils.guardLet(this.templateContainer?.flatten(), arr => arr[arr.length - 1])?.segment?.id;
							this.newSegmentType = s;
						}
					} as CommandPaletteAction;
				});
				
				actions.push(...[
					{
						group: 'Template Editor',
						label: 'Expand all',
						icon: this.Icons.CHEVRON_DOWN,
						title: 'Expand all items',
						command: () => {
							this.toggleAll(true);
						}
					},
					{
						group: 'Template Editor',
						label: 'Collapse all',
						icon: this.Icons.CHEVRON_UP,
						title: 'Collapse all items',
						command: () => {
							this.toggleAll(false);
						}
					}
				]);
				
				this.registerActions(...actions);
			});
		
		this._templateContainerSubject.pipe(
			takeUntil(this.onDestroy$),
			(source) => {
				let registration: CommandPaletteRegistration | undefined;
				return source.pipe(
					tap(container => {
						registration?.unregister();
						registration = undefined;
						if (!container) {
							return;
						}
						if (container.templateTypeId !== TemplateTypeId.INSPECTION && !(container.flatten()?.some(e => e.segment?.type?.id === TemplateSegmentTypeId.SUB_TEMPLATE) ?? false)) {
							return;
						}
						
						const name = container.templateTypeId === TemplateTypeId.OPERATIONAL ? 'Subtemplate' : 'Section';
						registration = this.registerActions({
							group: 'Template Segments',
							label: name,
							title: `Create new ${name}`,
							tags,
							icon: this.Icons.PLUS,
							command: () => {
								this.showSectionEditDialog({ parent: this.lastSectionNode.value, relativeTo: 'after', editing: null });
							}
						});
					})
				)
			}
		).subscribe(() => {
			const scrollTo = Utils.parseNumberOrUndefined(this.scrollToSegment);
			const node = this.templateContainer?.getById(scrollTo);
			
			if (node != null) {
				this.toggleAllUp(true, node);
				setTimeout(() => {
					this.scrollToSegment = undefined;
					const el = document.querySelector(`#${this.getNodeIdValue(node.segment?.id)}`);
					if (el == null) {
						return;
					}
					if (Utils.isElementOutsideOfViewport(el)) {
						el.scrollIntoView({
							behavior: 'auto',
							block: 'center',
						});
					}
				});
			}
		});
		
		this.templateEditorService.onConfirm$.pipe(
			takeUntil(this.onDestroy$)
		)
			.subscribe(c => {
				this.confirmationService.confirm(c);
			});
		
		this.segmentClipboard.onDelete$.pipe(
			takeUntil(this.onDestroy$),
			switchMap((ids) => {
				return this.quickRefresh().pipe(map(() => ids));
			})
		)
			.subscribe((deleted) => {
				if (!this._segments) return;
				
				const deletedMap = Utils.arrayToMap(deleted, 'id');
				this._segments = this._segments.filter(s => !(s.id in deletedMap) || s.updated != deletedMap[s.id].updated);
				this.updateContainerFromSegments();
			});
		
		this.segmentClipboard.onChange$.pipe(
			takeUntil(this.onDestroy$)
		)
			.subscribe((selection) => {
				this.clipboardHasSelection = selection.length > 0;
				this.clipboardHasSection = selection.some(n => TemplateSegment.isType(n.data, TemplateSegmentTypeId.SUB_TEMPLATE));
			});
		
		this.templateChangeSubject.pipe(
			takeUntil(this.onDestroy$),
			switchMap(template => {
				this.loading = true;
				this.hadError = false;
				return this.templateService.fetchTemplateSegments(template.id)
					.pipe(catchError((err) => {
						this.hadError = true;
						this.sendError(err, {
							suppressErrorToast: true
						});
						return [];
					}))
			}),
		)
			.subscribe((arr) => {
				if (!this.hadError) {
					this._segmentRefresh = new Date();
				}
				
				this._segments = arr;
				this.updateContainerFromSegments();
				this.loading = false;
			});
		
		this.templateChangeSubject.next(this._template);
		this.hadInit = true;
	}
	
	ngAfterViewInit() {
		this.uiTree.onNodeClick = Utils.closure(() => {
			const original = this.uiTree.onNodeClick.bind(this.uiTree);
			
			return function(this: Tree, ...params: Parameters<typeof original>) {
				const event = Nullish.of(params[0] as MouseEvent);
				let current = Nullish.of(event?.target as HTMLElement);
				
				let continueChain = false;
				
				// Checks whether the click happened on the checkbox instead of allowing the entire node to be clickable to be selected
				while (!continueChain && current && !current.classList.contains('p-treenode-content')) {
					continueChain = current.classList.contains('p-checkbox');
					current = current?.parentElement;
				}
				
				if (continueChain) {
					original(...params);
				}
			}
		}, this.uiTree);
		
		this.uiTree.allowDrop = Utils.closure(() => {
			const self = this;
			const original = this.uiTree.allowDrop.bind(this.uiTree);
			
			return function (this: Tree, ...params: Parameters<typeof original>) {
				const dragNode = params[0] as Optional<EditorTreeNode>;
				const dropNode = params[1] as Optional<EditorTreeNode>;
				const event = params[3];
				
				const dropSiblings = Utils.guardLet(dropNode?.parent, parent => {
					const idx = parent.children.indexOf(dropNode!);
					if (idx < 0) return undefined;
					return {
						before: parent.children[idx - 1],
						after: parent.children[idx + 1]
					}
				});
				
				const dragSegment = dragNode?.data;
				const dropSegment = dropNode?.data;
				
				// Whether the current event is targeting the drop points in-between the tree nodes
				const isDropPoint = (event?.target as HTMLElement)?.classList?.contains?.('p-treenode-droppoint') ?? false;
				const isFirstDropPoint = isDropPoint && (event!.target as HTMLElement).parentElement?.children.item(0) === event!.target;
				
				// Whether the current drop point falls below a standard segment
				const dropPointBelowSegment = isDropPoint && !!dropSiblings?.before?.data &&
					!TemplateSegment.isType(dropSiblings?.before?.data, TemplateSegmentTypeId.SUB_TEMPLATE) &&
					isFirstDropPoint
				
				const dropIsSubTemplate = TemplateSegment.isType(dropSegment, TemplateSegmentTypeId.SUB_TEMPLATE);
				const dragIsSubTemplate = TemplateSegment.isType(dragSegment, TemplateSegmentTypeId.SUB_TEMPLATE);
				
				// Do not allow dropping between sections for standard segments or onto drop points that are
				if (!dragIsSubTemplate && dropIsSubTemplate && isDropPoint && !dropPointBelowSegment) {
					return false;
				}
				
				// Do not allow dropping sections onto standard segments
				if (!dropIsSubTemplate && dragIsSubTemplate) {
					return false;
				}
				
				// Do not allow dropping into the same place
				if (isFirstDropPoint && dropSiblings?.before === dragNode) {
					return false;
				}
				
				//Do not allow standard segments to be dropped on standard segments
				if (!dragIsSubTemplate && !dropIsSubTemplate && !isDropPoint) {
					return false;
				}
				
				// Allow dropped segments on parent drop points if the sibling before the drop node belongs to a non-section segment.
				if (dropIsSubTemplate && !dragIsSubTemplate && isDropPoint && dropSiblings?.before.data && !TemplateSegment.isType(dropSiblings?.before?.data, TemplateSegmentTypeId.SUB_TEMPLATE)) {
					return original(...params);
				}
				
				// Do not allow drops onto parents, allow sections on droppoints
				if (dragNode?.parent === dropNode && (!dragIsSubTemplate || !isDropPoint)) {
					return false;
				}
				
				if (self.templateType === TemplateTypeId.OPERATIONAL) {
					//Do not allow segments to be dropped outside their own sub templates
					if (dragNode?.parent !== dropNode?.parent) {
						return false;
					}
					
					//Do not allow sub templates to be dropped onto each other
					if (dragIsSubTemplate && dropIsSubTemplate && !isDropPoint) {
						return false;
					}
				}
				
				
				return original(...params);
			}
		}, this.uiTree);
	}
	
	onSelectClicked({ originalEvent, node }: { originalEvent: PointerEvent, node: EditorTreeNode }) {
		if (!node.leaf) {
			return;
		}
		
		const parentChildren = node.parent?.children ?? [];
		
		if (parentChildren.length === 0 || this.selected.length <= 1) {
			return;
		}
		
		const selectionIdx = this.selected.findIndex(s => s.data.id == node.data?.id);
		
		if (selectionIdx < 0 || !originalEvent.shiftKey) {
			return;
		}
		
		const selectedMap = Utils.arrayToMap(this.selected, d => d.data.id);
		
		const endIdx = parentChildren.indexOf(node);
		
		let startIdx = -1;
		for (let i = endIdx - 1; i >= 0; i--) {
			const n = parentChildren[i];
			const id = n.data?.id;
			
			if (id == null) {
				continue;
			}
			
			if (selectedMap[id]) {
				startIdx = i;
				break;
			}
		}
		
		if (startIdx < 0) return;
		
		const predicate = (n: EditorTreeNode): n is ClipboardTreeNode => {
			return n.data != null && n.containerEntry != null;
		}
		
		const toSelect = parentChildren
			.slice(startIdx + 1, endIdx)
			.filter(predicate);
		
		this.selected = [...this.selected, ...toSelect];
	}
	
	/**
	 * Shows the section edit dialog
	 * @param params Provide null to create a new section at the top of the template to replace the 'Intro to template' section
	 */
	showSectionEditDialog({ parent, relativeTo, editing }: ShowSectionEditDialogParams) {
		let form: TemplateSectionEditorForm | TemplateSubTemplateEditorForm;
		let header: string;
		let callback: (val: MapTypeCallback<typeof form>) => void;
		
		const segment = OptionType.nonNull(editing)
			.map(n => n.data)
			.nonNullable()
			.castAsType(TemplateSegment.typePredicate(TemplateSegmentTypeId.SUB_TEMPLATE))
			.getOrProvider(() => {
				if (editing != null && editing.data != null) {
					throw new Error('Editing segment is not a sub template');
				}
				
				return undefined;
			});
		
		form = createEditorForm(segment, this.templateType === TemplateTypeId.INSPECTION ? 'section' : 'subtemplate');
		header = editing ? `Editing ${this.getName(segment)}` : `New ${this.templateType === TemplateTypeId.OPERATIONAL ? 'Subtemplate' : 'Section'}`;
		callback = (val) => {
			const options = Utils.arrayToMap(Object.entries(val), entry => [entry[0], entry[1].value]);
			const maybeHighlight = (val: unknown) => {
				OptionType.first(
					OptionType.nonNull((val as any)?.id)
						.castAsType(Utils.isValidNumber),
					OptionType.of(val).castAsType(Utils.isValidNumber),
				)
					.whenSome((id) => {
						this.scrollToSegment = id;
						this.highlightNode(id);
					})
			}
			
			if (segment) {
				this.templateService.updateTemplateSegment(segment, options)
					.subscribe(updated => {
						maybeHighlight(updated);
						this.replaceSegment(updated);
						this.updateContainerFromSegments();
					})
			}
			else if (parent?.containerEntry) {
				this.templateEditorService.createNewSectionRelativeToSection(parent.containerEntry, relativeTo, options)
					.subscribe((s) => {
						maybeHighlight(s);
						this.refresh();
					})
			}
			else {
				this.templateEditorService.createSection({
					...options,
					sortOrder: -1,
				}).subscribe((s) => {
					maybeHighlight(s);
					this.refresh();
				});
			}
		};
		
		this.formDialogService.show(
			new FormInputDialogForm(form)
				.setHeader(header)
				.setPosition('top')
				.setCompleteButtonOptions({
					label: 'Save',
					class: 'p-button-success'
				})
				.setOnComplete(callback)
		);
		
		function createEditorForm<Type extends EditorFormType>(editing: TypedTemplateSegment<TemplateSegmentTypeId.SUB_TEMPLATE> | undefined, type: Type): EditorFormMap[Type] {
			const name = new StringInputDialogEntry(new FormControl(editing?.name ?? '', { validators: Validators.required }))
				.setLabel('Name')
				.setPlaceholder('Required');
			
			if (type === 'section') {
				return {
					name,
					requireCommentOnAbnormal: new SelectorInputDialogEntry(new FormControl<boolean | null>(editing?.requireCommentOnAbnormal ?? null), [
						SelectorItem.with(null, 'Inherit'),
						SelectorItem.with(true, 'Required'),
						SelectorItem.with(false, 'Do not require')
					])
						.setLabel('Require comment on abnormal'),
					print: new BooleanInputDialogEntry(new FormControl(editing?.print ?? false))
						.setLabel('Print')
				} as EditorFormMap[Type];
				
			}
			return {
				name,
				turnover: new BooleanInputDialogEntry(new FormControl(editing?.turnover ?? false))
					.setLabel('Turnover'),
				clearTime: new BooleanInputDialogEntry(new FormControl(editing?.clearTime ?? false))
					.setLabel('Clear Time On Add')
			} as EditorFormMap[Type];
		}
	}
	
	showDialog(template: TemplateRef<any>, context: Nullable<DialogContext>, options?: DialogOptions) {
		this.dialogTemplate = template;
		if (context) {
			this.dialogContext = {
				...context,
				...(options ?? {})
			};
		} else {
			this.dialogContext = null;
		}
	}
	
	showFormulaEditor(segment: TemplateSegment) {
		if (this.templateContainer == null) {
			throw new Error('Template container is null');
		}
		
		const scope: FormulaEditorScope = {
			segment,
			templateContainer: this.templateContainer,
		};
		
		this.showDialog(this.calculationTemplate, this.createTemplateContext(scope), {
			header: `Formula for ${segment.name}`,
			closeDialog: () => this.hideDialog(),
			style: { width: '100%', maxWidth: '1500px' }
		});
	}
	
	hideDialog() {
		delete this.dialogTemplate;
		this.dialogContext = null;
	}
	
	getName(segment?: TemplateSegment, defaultVal?: string) {
		return segment?.name ?? defaultVal ?? `Intro to ${this.template.name}`;
	}
	
	toggleAll(expanded: boolean, fromNode?: EditorTreeNode) {
		const nodes = OptionType.nonNull(fromNode)
			.map((n) => [n, ...n.children])
			.getOrElse(this.treeNodes);
		
		nodes.forEach(node => {
			node.expanded = expanded;
			
			node.children.forEach((node) => {
				this.toggleAll(expanded, node);
			});
		});
		
		setTimeout(() => {
			this.changeDetectorRef.detectChanges();
		});
	}
	
	toggleAllUp(expanded: boolean, fromNode: EditorTreeNode | ReadonlyContainerEntry) {
		let current = Optional.of(fromNode);
		while (current != null) {
			if ('data' in current) {
				this.segmentOpenMap[current.data?.id ?? -1] = expanded;
			}
			else if ('segment' in current) {
				this.segmentOpenMap[current.segment?.id ?? -1] = expanded;
			}
			
			current = current.parent;
		}
		
		setTimeout(() => {
			this.changeDetectorRef.detectChanges();
		})
	}
	
	getNodeHasValue(node: EditorTreeNode): { id: number, node: HasValue<EditorTreeNode> } {
		const id = node.data?.id ?? -1;
		let hasValue = this.nodeMap[id];
		
		if (hasValue) {
			if (hasValue.value !== node) {
				hasValue.value = node;
			}
		}
		else {
			hasValue = { value: node };
		}
		
		this.nodeMap[id] = hasValue;
		return { id, node: hasValue };
	}
	
	getMenuModel(input: EditorTreeNode, type: MenuType): MenuItem[] {
		const { id, node } = this.getNodeHasValue(input);
		let menuValue = this.segmentMenuMap[id];
		if (menuValue) {
			return menuValue.value[type];
		}
		
		menuValue = new ComputedValue({
			reference: node,
			get: (node): Record<MenuType, MenuItem[]> => {
				const copyItems = new Array<MenuItem>();
				const overflowItems = new Array<MenuItem>();
				
				const segment = node.data;
				const segmentIsSection = TemplateSegment.isType(segment, TemplateSegmentTypeId.SUB_TEMPLATE);
				
				const getSiblings = () => {
					let siblings = node.parent?.children;
					if (siblings == null) {
						siblings = this.treeNodes;
					}
					
					return siblings;
				}
				
				const getIndexOf = () => {
					let siblings = getSiblings();
					if (siblings == null) {
						siblings = this.treeNodes;
					}
					
					const indexOf = siblings.indexOf(node);
					if (indexOf < 0) {
						throw new Error('Cannot find self among siblings');
					}
					
					return indexOf;
				}
				
				const getNextSibling = () => {
					const siblings = getSiblings();
					const indexOf = getIndexOf();
					return siblings[indexOf + 1];
				}
				
				const copyMenuItem: MenuItem = Utils.defineGetters({
					label: 'Duplicate Selected Items',
					title: segmentIsSection
						? `Duplicate selected items and place them at the start of ${this.getName(segment)}`
						: `Duplicate selected items and place them after ${this.getName(segment)}`,
					icon: this.Icons.CLONE,
					command: () => {
						let toCopy = this.segmentClipboard.value
							.map(n => n.containerEntry)
							.filter(n => n?.segment != null);
						
						if (toCopy.length === 0) {
							return;
						}
						
						let parent: ReadonlyContainerEntry | null;
						if (segmentIsSection || segment?.id == null) {
							parent = node.containerEntry ?? null;
						}
						else {
							parent = (node.parent as EditorTreeNode)?.containerEntry ?? null;
						}
						
						if (parent == null) {
							throw new Error('Parent is not a valid container entry');
						}
						
						// Do not copy sub templates
						if (this.templateType === TemplateTypeId.OPERATIONAL) {
							toCopy = toCopy.filter(n => n.segment!.type.id !== TemplateSegmentTypeId.SUB_TEMPLATE);
						}
						
						let insertAt = Optional.of<number>();
						if (!segmentIsSection) {
							insertAt = getIndexOf();
							if (!segmentIsSection && segment) {
								insertAt += 1;
							}
						}
						
						this.templateEditorService.copyIntoParent(toCopy, parent, insertAt).add(() => {
							this.refresh();
							this.segmentClipboard.clear();
						});
					}
				}, {
					'disabled': {
						get: () => this.segmentClipboard.value.length === 0
					}
				});
				
				copyItems.push(copyMenuItem);
				
				if (this.templateType !== TemplateTypeId.OPERATIONAL || !segmentIsSection) {
					const moveMenuItem: MenuItem = Utils.defineGetters({
						label: 'Move Selected Items',
						title: segmentIsSection
							? `Moved selected items and place them at the start of ${this.getName(segment)}`
							: `Move selected items and place them after ${this.getName(segment)}`,
						icon: this.Icons.ARROW_DOWN,
						command: () => {
							const toMove = this.segmentClipboard.value
								.map(n => n.containerEntry)
								.filter(n => n?.segment?.id != null && !TemplateSegment.isType(n.segment, TemplateSegmentTypeId.SUB_TEMPLATE));
							
							let parent: ReadonlyContainerEntry | null;
							if (segmentIsSection || segment?.id == null) {
								parent = node.containerEntry ?? null;
							}
							else {
								parent = (node.parent as EditorTreeNode)?.containerEntry ?? null;
							}
							
							if (parent == null) {
								throw new Error('Parent is not a valid container entry');
							}
							
							let insertAt = Optional.of<number>();
							if (!segmentIsSection) {
								insertAt = getIndexOf();
								if (!segmentIsSection && segment) {
									insertAt += 1;
								}
							}
							
							this.templateEditorService.moveIntoParent(toMove, parent, insertAt).add(() => {
								this.segmentClipboard.clear();
							});
						}
					}, {
						'disabled': {
							get: () => {
								let disabled = this.segmentClipboard.value.length === 0 ||
									this.segmentClipboard.hasMultiTemplate ||
									this.segmentClipboard.has(segment?.id ?? -1);
								
								if (disabled) {
									return true;
								}
								else if ((segmentIsSection || segment?.id == null) && node.children[0].data?.id === this.segmentClipboard.value[0]?.data?.id) {
									return true;
								}
								else if (Utils.guardLet(getNextSibling(), next => this.segmentClipboard.value[0]?.data.id == next?.data?.id) ?? false) {
									return true;
								}
								
								switch (this.templateType) {
									case TemplateTypeId.OPERATIONAL: {
										const subTemplateId = node.parent?.data?.id ?? node.data?.id;
										
										if (!Utils.isValidNumber(subTemplateId)) {
											break;
										}
										
										const selectedSubTemplateIds = this.segmentClipboard.value
											.map(e => e.parent?.data?.id ?? e.data.id)
											.filter(Utils.isValidNumber);
										
										// Disable as we do not allow moving between sub templates
										if (selectedSubTemplateIds.some(s => s != subTemplateId)) {
											disabled = true;
										}
									}
								}
								
								return disabled;
							}
						}
					});
					
					copyItems.push(moveMenuItem);
				}
				
				if (segmentIsSection || segment == null) {
					const editMenuItem: MenuItem = {
						label: 'Edit',
						icon: this.Icons.PENCIL,
						title: `Edit ${this.getName(segment)}`,
						command: () => {
							Utils.guard(node, node => {
								this.editSectionNode(node);
							})
						}
					};
					
					let toAdd: MenuItem[] = [
						editMenuItem
					];
					
					if (node.children.length > 0) {
						toAdd.push(
							{
								separator: true
							},
							{
								label: 'Expand Children',
								icon: this.Icons.CHEVRON_DOWN,
								title: `Expand all children of ${this.getName(segment)}`,
								command: () => this.toggleAll(true, node),
							},
							{
								label: 'Collapse Children',
								icon: this.Icons.CHEVRON_UP,
								title: `Collapse all children of ${this.getName(segment)}`,
								command: () => this.toggleAll(false, node)
							},
						);
					}
					
					overflowItems.push(...toAdd);
				}
				
				
				if (segment?.obsolete === false) {
					if (overflowItems.length > 0) {
						overflowItems.push({
							separator: true
						})
					}
					
					overflowItems.push(
						{
							label: 'Delete',
							icon: this.Icons.TRASH,
							title: `Delete ${this.getName(segment, "this " + segment?.type.name + " segment")}`,
							command: () => {
								this.onSegmentDelete(node);
							}
						}
					);
				}
				else if (segment?.obsolete) {
					if (overflowItems.length > 0) {
						overflowItems.push({
							separator: true
						})
					}
					overflowItems.push(
						{
							label: 'Restore',
							icon: this.Icons.REFRESH,
							title: `Restore ${this.getName(segment, "this " + segment?.type.name + " segment")}`,
							command: () => {
								this.onSegmentRestore(segment!);
							}
						}
					);
				}
				
				return {
					overflow: overflowItems,
					copy: copyItems
				};
			}
		});
		
		this.segmentMenuMap[id] = menuValue;
		return menuValue.value[type];
	}
	
	editSectionNode(node: EditorTreeNode) {
		if (node.data == null && node === this.treeNodes[0]) {
			// Intro to template sections
			this.showSectionEditDialog({ parent: null, editing: node, relativeTo: 'after' });
		}
		else if (TemplateSegment.isType(node.data, TemplateSegmentTypeId.SUB_TEMPLATE)) {
			this.showSectionEditDialog({parent : node, relativeTo : 'into', editing : node})
		}
		else {
			throw new Error(`Cannot edit ${node.label} as type section/subtemplate`);
		}
	}
	
	getSectionAddMenu(section: EditorTreeNode): MenuItem[] {
		const { id, node } = this.getNodeHasValue(section);
		let menuValue = this.sectionMenuMap[id];
		
		if (menuValue) {
			return menuValue.value;
		}
		
		menuValue = new ComputedValue({
			reference: node,
			get: (node) => {
				const segment = node.data;
				const templateIsInspection = this.templateType === TemplateTypeId.INSPECTION;
				const items: MenuItem[] = [];
				
				const addItem: MenuItem = {
					label: `Create a new ${templateIsInspection ? 'section' : 'subtemplate'} after ${this.getName(segment)}`,
					title: `Create a new ${templateIsInspection ? 'section' : 'subtemplate'} after ${this.getName(segment)}`,
					command: () => this.showSectionEditDialog({editing: null, parent : node, relativeTo : 'after'})
				}
				
				const subItem: MenuItem = {
					label: `Create a new section in ${this.getName(segment)}`,
					title: `Create a new section at the end of ${this.getName(segment)}`,
					disabled: (segment?.level ?? 5) >= 5,
					command: () => this.showSectionEditDialog({editing: null, parent : node, relativeTo : 'into'})
				}
				
				const addSegment: MenuItem = {
					label: `Create a new segment in ${this.getName(segment)}`,
					title: `Create a new segment at the end of ${this.getName(segment)}`,
					command: () => {
						this.segmentTypes.pipe(
							take(1)
						).subscribe((types) => {
							this.newPrevId = segment?.id;
							this.newSegmentType = types[0].value;
						});
					}
				}
				
				items.push(addItem);
				
				if (templateIsInspection && segment) {
					items.push(subItem);
				}
				
				items.push({
						separator: true
					},
					addSegment
				);
				
				return items;
			}
		});
		
		this.sectionMenuMap[id] = menuValue;
		return menuValue.value;
	}
	
	setNewSegmentType(type: TemplateSegmentType | undefined, previousId?: number | 'last') {
		this.segmentEditor.protectSegmentNavigation(() => {
			this.segmentEditor.segment = type ? this.createNewSegment(type, previousId) : null;
			this._newSegmentType = type;
		}, () => {
			// Writes the value back to the dropdown to stay consistent with the value not changing
			this.dropdown.writeValue(this._newSegmentType);
		});
	}
	
	createNewSegment<T extends TemplateSegmentTypeId>(type: TemplateSegmentType<T>, previousId?: number | 'last'): TemplateSegmentEditorType<T> {
		return Utils.also({
			type: type as TypedTemplateSegmentType<T>,
			template: this.template
		} as TemplateSegmentEditorType<T>, segment => {
			OptionType.nonNull(previousId)
				.fork({
					some: (prevId) => {
						if (prevId === 'last') {
							return Utils.guardLet(this.templateContainer?.flatten(), (entries) => {
								return entries[entries.length - 1]?.segment?.id;
							})
						}
						return prevId;
					},
					none: () => {
						return this.newPrevId;
					}
				})
				.castAsType(this.isValidNumber)
				.whenSome((prevId) => {
					this._newPrevId = prevId;
					segment.prevItemId = prevId;
				});
			
		});
	}
	
	refresh() {
		this.template = this.template;
	}
	
	// Uses the last segment fetch time to update only the segments that have been updated
	quickRefresh() {
		const templateId = this.template.id;
		const refreshDate = this._segmentRefresh;
		
		if (!refreshDate) {
			this.refresh();
			return of();
		}
		
		const k = this.templateService.queryTemplateSegments({
			updatedFrom: refreshDate,
			template: templateId,
		}).pipe(
			takeUntil(this.onDestroy$),
			switchMap((results) => {
				const op = OptionType.of(results)
					.filter(() => this.template.id == templateId && this._segmentRefresh == refreshDate);
				
				if (op.isSome()) {
					return of(op);
				}
				
				return of(op);
			}),
		)
			.subscribe((opt) => {
				opt.filter(() => this.template.id == templateId && this._segmentRefresh == refreshDate)
					.whenSome((value) => {
						value.forEach((item) => {
							this.replaceSegment(item);
						});
						
						this._segmentRefresh = new Date();
					});
			});
		
		return new Observable((subscriber) => {
			k.add(() => {
				subscriber.next();
				subscriber.complete();
			});
		});
	}
	
	hasProperties(type: TemplateSegmentType, ...types: TemplateSegmentPropertyKey[]) {
		const hasKeys = Object.keys(type.properties).length > 0;
		if (types.length) {
			return types.some(key => doesTemplateSegmentPropertiesSupportProperty({ key, properties: type.properties, templateType: this.templateType }));
		}
		else {
			return hasKeys;
		}
	}
	
	templateSegmentSupportsProperty(key: TemplateSegmentPropertyKey, segment: TemplateSegment): boolean {
		return doesTemplateSegmentPropertiesSupportProperty({ key, properties: segment.type.properties, templateType: this.templateType });
	}
	
	saveSegmentCalculation(segment: TemplateSegment, formulaScope: Formula.Scope): void {
		const formula = Formula.scopeToString(formulaScope);
		const update: JSON.Object = {
			formula
		}
		
		if ('decimalPlaces' in formulaScope && formulaScope.decimalPlaces !== undefined) {
			update['decimalPlaces'] = formulaScope.decimalPlaces;
		}
		
		this.templateService.updateTemplateSegment(segment, update).subscribe(updated => {
			this.replaceSegment(updated);
			this.updateContainerFromSegments();
		});
	}
	
	onSegmentSaved(segment: TemplateSegment) {
		if (!this._segments) {
			throw new Error('Trying to update segments when none exists');
		}
		
		this.scrollToSegment = segment.id;
		this.highlightNode(segment.id);
		
		const editingSegment = this.segmentEditor.segment;
		this.segmentEditor.segment = undefined;
		
		const idx = this._segments.findIndex(s => s.id === segment.id);
		if (idx >= 0) {
			this._segments[idx] = segment;
		}
		else if (Utils.isValidNumber(editingSegment?.prevItemId)) {
			const insertionIdx = Utils.guardLet(editingSegment?.prevItemId, prevId => {
				if (!this.isValidNumber(prevId)) {
					return;
				}
				return this._segments?.findIndex(s => s.id === prevId);
			}) ?? -1;
			
			if (insertionIdx >= 0) {
				this._segments.splice(insertionIdx + 1, 0, segment);
			}
		}
		else {
			this.refresh();
			return;
		}
		
		this.updateContainerFromSegments();
	}
	
	onSegmentRestore(segment: TemplateSegment, bypassCheck = false) {
		if (!bypassCheck) {
			this.confirmationService.confirm({
				key: this.confirmPopupKey,
				header: `Restore ${segment.longName}`,
				message: `Are you sure you want to restore "${segment.longName}"?`,
				accept: () => this.onSegmentRestore(segment, true),
				acceptLabel: 'Confirm',
				rejectLabel: 'Cancel',
				acceptButtonStyleClass: 'p-button-success',
				rejectButtonStyleClass: 'p-button-secondary',
				defaultFocus: 'reject'
			});
			return;
		}
		
		this.templateService.updateTemplateSegment(segment, { obsolete: false })
			.subscribe((newSegment) => {
				this.replaceSegment(newSegment);
				this.updateContainerFromSegments();
		});
	}
	
	onSegmentDelete(node: EditorTreeNode, bypassCheck = false) {
		const segment = node.data;
		if (segment == null) {
			throw new Error('Cannot delete null segment');
		}
		
		let header = `Delete ${segment.longName}`;
		let message = `Are you sure you want to delete "${segment.longName}"? If the segment is in use it will
				be become obsolete and can be restored.`
		
		const isSubTemplate = this.templateType === TemplateTypeId.OPERATIONAL && segment.type.id === TemplateSegmentTypeId.SUB_TEMPLATE;
		
		if (isSubTemplate) {
			header = `Delete Subtemplate ${segment.name}`;
			message = `Are you sure you want to delete "${segment.name}"? This will delete all segments in the subtemplate.`;
		}
		
		if (!bypassCheck) {
			this.confirmationService.confirm({
				key: this.confirmPopupKey,
				header,
				message,
				accept: () => this.onSegmentDelete(node, true),
				acceptLabel: 'Confirm',
				rejectLabel: 'Cancel',
				acceptButtonStyleClass: 'p-button-danger',
				rejectButtonStyleClass: 'p-button-secondary',
				defaultFocus: 'reject'
			});
			return;
		}
		
		const toDelete = [segment];
		if (isSubTemplate) {
			toDelete.push(...node.children.map(n => n.data).filter(Nullable.isNotNull));
		}
		
		const toDeleteIdMap = Utils.arrayToMap(toDelete, 'id');
		
		const now = new Date();
		this.templateService.deleteTemplateSegment(...toDelete)
			.pipe(
				takeUntil(this.onDestroy$),
				switchMap(() => this.quickRefresh())
			)
			.subscribe(() => {
				this.segmentClipboard.remove(...toDelete.map(s => s.id));
				this._segments = this._segments?.filter(s => !(s.id in toDeleteIdMap) || new Date(s.updated) > now);
				this.updateContainerFromSegments();
		})
	}
	
	getReferenceLabelForVariable(variable: Formula.Variable) {
		return Formula.getReferenceLabelForVariable(variable, this.segmentMap);
	}
	
	focusDialog() {
		setTimeout(() => {
			const el: Element = this.dialog?.el?.nativeElement;
			if (el == null) {
				return;
			}
			
			el.querySelector('input')?.focus();
			this.changeDetectorRef.detectChanges();
		});
	}
	
	getNodeIdValue(nodeId: number | undefined) {
		return this.keyedId('segment', nodeId ?? -1);
	}
	
	highlightNode(segmentId: number | undefined) {
		const id = segmentId ?? -1;
		this.highlightSegments.add(id);
		this.changeDetectorRef.detectChanges();
	}
	
	onHighlightEnd(event: AnimationEvent) {
		const t = event.target;
		if (t instanceof HTMLElement) {
			const el = t.querySelector('.highlight');
			if (!(el instanceof HTMLElement)) {
				return;
			}
			
			const id = Utils.parseNumberOrUndefined(el.dataset.segmentId);
			if (id != null) {
				this.highlightSegments.delete(id);
				this.changeDetectorRef.detectChanges();
			}
		}
	}
	
	private replaceSegment(segment: TemplateSegment) {
		if (!this._segments) return;
		
		const foundIdx = this._segments.findIndex(s => s.id === segment.id);
		if (foundIdx >= 0) this._segments[foundIdx] = segment;
	}
	
	private sortSegmentProperties(segment: TemplateSegment) {
		const userSortConfig: MultiSortConfig<LogUser>[] = [
			{ key: v => v.user.lastName },
			{ key: v => v.user.firstName }
		];
		
		segment.users?.sort(Utils.createMultiSortFunction(userSortConfig));
		segment.editUsers?.sort(Utils.createMultiSortFunction(userSortConfig));
		segment.userGroups?.sort(Utils.createSortFunction('name', 'asc'));
		return segment;
	}
	
	private updateContainerFromSegments() {
		if (!this._segments) {
			return;
		}
		
		this._segments.forEach(s => this.sortSegmentProperties(s));
		this.templateContainer = this.templateService.buildTemplateContainer(this.template, this._segments);
	}
	
	readonly onDrop = this.templateEditorService.onDrop.bind(this.templateEditorService);
	protected readonly TemplateTypeId = TemplateTypeId;
}

export type EditorTreeNode = TypedTreeNode<Optional<TemplateSegment>> & {
	containerEntry?: ReadonlyContainerEntry
}

export type EditorFooterTreeNode = {
	prevId: number | undefined;
} & EditorTreeNode;

interface ShowSectionEditDialogParams {
	parent: EditorTreeNode | null;
	relativeTo: "into" | "after";
	editing: EditorTreeNode | null;
}

type EditorFormType = 'section' | 'subtemplate';
interface EditorFormMap extends Record<EditorFormType, FormInputDialogEntryMap> {
	section: TemplateSectionEditorForm,
	subtemplate: TemplateSubTemplateEditorForm
}
interface TemplateSectionEditorForm extends FormInputDialogEntryMap {
	name: StringInputDialogEntry,
	requireCommentOnAbnormal: SelectorInputDialogEntry<boolean>,
	print: BooleanInputDialogEntry
}

interface TemplateSubTemplateEditorForm extends FormInputDialogEntryMap {
	name: StringInputDialogEntry,
	turnover: BooleanInputDialogEntry,
	clearTime: BooleanInputDialogEntry
}

type DialogContext = BasicRecord<any> & {
	$implicit: any
} & DialogOptions;

type DialogOptions = {
	closeDialog?: () => void,
	style?: Partial<CSSStyleDeclaration>,
	header?: string
}

type MenuType = 'overflow' | 'copy';